import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetProgramChecklistQuery, useGetRunChecklistQuery } from "../../utils/api/endpoints/checklists/checklistApi";
import { useGetProgramQuery } from "../../utils/api/endpoints/programsApi";
import { reportsApi } from "../../utils/api/endpoints/reportsApi";
import type { CTBReport } from "../../utils/api/endpoints/reports/parseReports";
import { ReportState } from "../../utils/api/endpoints/reports/reports";
import usePageTitle from "../../utils/hooks/usePageTitle";
import useProgramRuns from "../../utils/hooks/programs/useProgramRuns";
import toast from "react-hot-toast";
import { generateChecklistPDF } from "../../components/checklists/pdf/ChecklistPDFDocument";
import { 
  FiCheckCircle, FiClock, FiAlertCircle, FiLock, FiXCircle, 
  FiHome, FiChevronRight, FiPlay, FiLayers, FiSearch, FiDownload
} from "react-icons/fi";
import { ConfirmationModal } from "../../components/checklists/ConfirmationModal";

const BusinessChecklistView: React.FC = () => {
  const { programId, runId, checklistId } = useParams<{
    programId: string;
    runId?: string;
    checklistId: string;
  }>();
  const navigate = useNavigate();
  const { runs } = useProgramRuns(parseInt(programId!));
  const currentRun = runs?.find(r => r.run_id === parseInt(runId!));

  usePageTitle("Checklist Overview");

  // Situational Data Fetching: Use run-specific query if runId is present
  const { data: programChecklistData, isLoading: programLoading } = useGetProgramChecklistQuery({
    programId: parseInt(programId!),
    checklistId: parseInt(checklistId!)
  }, { skip: !!runId });

  const { data: runChecklistData, isLoading: runLoading } = useGetRunChecklistQuery({
    runId: parseInt(runId!),
    checklistId: parseInt(checklistId!)
  }, { skip: !runId });

  const checklistData = runId ? runChecklistData : programChecklistData;
  const checklistLoading = runId ? runLoading : programLoading;

  const { data: program } = useGetProgramQuery(parseInt(programId!));

  const checklist = checklistData?.data;

  const [linkedReportsById, setLinkedReportsById] = useState<Record<number, CTBReport>>({});
  const [isLoadingLinkedReports, setIsLoadingLinkedReports] = useState(false);
  const [testFilter, setTestFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "COMPLETED" | "IN_PROGRESS" | "NOT_STARTED">("ALL");
  const [outcomeFilter, setOutcomeFilter] = useState<"ALL" | "PASSED" | "FAILED" | "NOT_APPLICABLE">("ALL");
  const [viewMode, setViewMode] = useState<"board" | "list">("list");
  const [expandedReports, setExpandedReports] = useState<Record<number, boolean>>({});
  const [fetchReport] = reportsApi.useLazyGetReportQuery();
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const executeDownloadPDF = async () => {
    setIsDownloadingPDF(true);
    const toastId = toast.loading("Generating Checklist PDF…");
    try {
      const programName = program?.title || "Program";
      const blob = await generateChecklistPDF({
        checklist: checklist!,
        programName,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = programName.replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 60);
      link.href = url;
      link.download = `Pentest_Checklist_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Checklist PDF downloaded", { id: toastId });
    } catch (err: any) {
      console.error("Checklist PDF generation failed:", err);
      toast.error(err?.message || "Failed to generate PDF", { id: toastId });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!checklist || !checklist.is_admin_reviewed || isDownloadingPDF) return;
    
    const checklistItems = checklist.items || [];
    const totalItems = checklistItems.length;
    const completedItems = checklistItems.filter(item => ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.status)).length;
    const percentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

    if (percentage < 100) {
      setShowConfirmModal(true);
    } else {
      await executeDownloadPDF();
    }
  };

  const uniqueCategories = React.useMemo(() => {
    const items = checklistData?.data?.items || [];
    const cats = new Set<string>();
    items.forEach((item: any) => {
      if (item.category) cats.add(item.category);
    });
    return Array.from(cats).sort();
  }, [checklistData]);

  const [filterCategory, setFilterCategory] = useState("ALL");

  useEffect(() => {
    if (!checklist || !checklist.items || checklist.items.length === 0) {
      setLinkedReportsById({});
      return;
    }

    const allIds = Array.from(
      new Set(
        (checklist.items as any[])
          .flatMap((item: any) => item.linked_report_ids || [])
          .filter((id: any) => typeof id === "number")
      )
    );

    if (allIds.length === 0) {
      setLinkedReportsById({});
      return;
    }

    let cancelled = false;

    const loadReports = async () => {
      try {
        setIsLoadingLinkedReports(true);
        const map: Record<number, CTBReport> = {};

        for (const id of allIds) {
          try {
            const report = await fetchReport(id).unwrap();
            map[id] = report;
          } catch (error) {
            console.error("Failed to load linked report for business checklist", id, error);
          }
        }

        if (!cancelled) {
          setLinkedReportsById(map);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLinkedReports(false);
        }
      }
    };

    loadReports();

    return () => {
      cancelled = true;
    };
  }, [checklist, fetchReport]);

  if (checklistLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-xl bg-white p-12 shadow-lg">
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-t-transparent"></div>
                <p className="text-gray-600 font-medium">Loading checklist...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-xl bg-white p-12 text-center shadow-lg">
            <FiAlertCircle className="mx-auto h-16 w-16 text-red-500" />
            <h3 className="mt-4 text-xl font-bold text-gray-800">Checklist Not Found</h3>
            <p className="mt-2 text-gray-600">
              This checklist may not be frozen or you don't have access to it.
            </p>
            <button
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="mt-6 rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Back to Program
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if checklist is frozen
  if (!checklist.is_frozen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-6">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-xl bg-white p-12 text-center shadow-lg">
            <FiLock className="mx-auto h-16 w-16 text-orange-500" />
            <h3 className="mt-4 text-xl font-bold text-gray-800">Checklist Not Available</h3>
            <p className="mt-2 text-gray-600">
              This checklist has not been frozen yet. It will be available once the QA/Admin team freezes it.
            </p>
            <button
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="mt-6 rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white transition-colors hover:bg-blue-700"
            >
              Back to Program
            </button>
          </div>
        </div>
      </div>
    );
  }

  const items = checklist.items || [];
  const categoryStatus = (checklistData?.data as any)?.categoryStatus || {};
  const totalItems = items.length;
  const completedItems = items.filter(item => ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.status)).length;
  const inProgressItems = items.filter(item => item.status === "IN_PROGRESS").length;
  const completionPercentage = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Helper function to determine outcome for an item
  const getItemOutcome = (item: any): "PASSED" | "FAILED" | "NOT_APPLICABLE" | "NOT_PERFORMED" => {
    if (item.status === "NOT_APPLICABLE") return "NOT_APPLICABLE";
    
    const performed =
      item.status === "COMPLETED" ||
      item.status === "FAILED";

    if (!performed) {
      return "NOT_PERFORMED";
    }

    const hasActiveLinkedReport =
      Array.isArray(item.linked_report_ids) &&
      (item.linked_report_ids as number[]).some((id: number) => {
        const report = linkedReportsById[id];
        if (!report) return true;
        const state = report.state;
        return (
          state !== ReportState.DELETED
        );
      });

    const failed =
      item.status === "FAILED" ||
      item.bug_found === true ||
      hasActiveLinkedReport;

    return failed ? "FAILED" : "PASSED";
  };

  // Filter items for board view (no status filter, only search and outcome)
  const filteredBoardItems = items.filter((item: any) => {
    const q = testFilter.trim().toLowerCase();
    const label = (item.title || item.category || "").toLowerCase();
    const matchesText = q ? label.includes(q) : true;

    const outcome = getItemOutcome(item);
    const matchesOutcome =
      outcomeFilter === "ALL" ||
      (outcomeFilter === "PASSED" && outcome === "PASSED") ||
      (outcomeFilter === "FAILED" && outcome === "FAILED") ||
      (outcomeFilter === "NOT_APPLICABLE" && outcome === "NOT_APPLICABLE");

    const matchesCategory = filterCategory === "ALL" || item.category === filterCategory;

    return matchesText && matchesOutcome && matchesCategory;
  });

  // Simple client-side filters for test list (text + status + outcome)
  const filteredGroupedItems = Object.entries(
    items.reduce((acc, item) => {
      const category = item.category || "Uncategorized";
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, typeof items>)
  ).reduce((acc, [category, categoryItems]) => {
    const q = testFilter.trim().toLowerCase();

    const visibleItems = categoryItems.filter((item: any) => {
      // Text filter on title/category
      const label = (item.title || item.category || "").toLowerCase();
      const matchesText = q ? label.includes(q) : true;

      // Status filter (only for list view)
      const matchesStatus =
        statusFilter === "ALL" || item.status === statusFilter;

      // Outcome filter (business pass/fail logic)
      const outcome = getItemOutcome(item);
      const matchesOutcome =
        outcomeFilter === "ALL" ||
        (outcomeFilter === "PASSED" && outcome === "PASSED") ||
        (outcomeFilter === "FAILED" && outcome === "FAILED") ||
        (outcomeFilter === "NOT_APPLICABLE" && outcome === "NOT_APPLICABLE");

      const matchesCategory = filterCategory === "ALL" || item.category === filterCategory;

      return matchesText && matchesStatus && matchesOutcome && matchesCategory;
    });

    if (visibleItems.length > 0) {
      acc[category] = visibleItems;
    }
    return acc;
  }, {} as Record<string, typeof items>);

  const getActiveLinkedReportIds = (item: any): number[] => {
    if (!item.linked_report_ids || item.linked_report_ids.length === 0) return [];

    return (item.linked_report_ids as number[]).filter((id: number) => {
      const report = linkedReportsById[id];

      if (!report) {
        // If report details are not loaded yet, treat as active
        return true;
      }

      const state = report.state;
      return state !== ReportState.DELETED;
    });
  };

  const canBusinessSeeReportDetails = (item: any): boolean => {
    const activeReportIds = getActiveLinkedReportIds(item);
    const hasActiveReports = activeReportIds.length > 0;
    
    // Business can see details if there are linked reports that have passed initial QA triage
    // Note: reviewed_by is removed from API for business roles
    return hasActiveReports;
  };


  // Business pass/fail logic:
  // - A test is considered "performed" when status is COMPLETED, NOT_APPLICABLE, or FAILED
  // - If a performed test has bug_found true or active linked reports (excluding Under Review / Deleted), it's counted as FAILED
  // - Performed tests without bugs are counted as PASSED
  const performedItems = items.filter(
    item =>
      item.status === "COMPLETED" ||
      item.status === "NOT_APPLICABLE" ||
      item.status === "FAILED"
  );

  const failedItems = items.filter(
    item =>
      item.status === "FAILED" ||
      item.bug_found === true ||
      getActiveLinkedReportIds(item).length > 0
  ).length;

  const passedItems = items.filter(
    item =>
      item.status === "COMPLETED" &&
      !item.bug_found &&
      getActiveLinkedReportIds(item).length === 0
  ).length;

  const naItems = items.filter(item => item.status === "NOT_APPLICABLE").length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
            <FiCheckCircle className="h-3 w-3" />
            Passed
          </span>
        );
      case "FAILED":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700">
            <FiXCircle className="h-3 w-3" />
            Failed
          </span>
        );
      case "IN_PROGRESS":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
            <FiClock className="h-3 w-3" />
            In Progress
          </span>
        );
      case "NOT_STARTED":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
            Not Started
          </span>
        );
      case "PENDING_REVIEW":
      case "REJECTED":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2.5 py-1 text-xs font-semibold text-yellow-700">
            Pending Review
          </span>
        );
      case "NOT_APPLICABLE":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500">
            N/A
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
            {status}
          </span>
        );
    }
  };

  const getCategoryStatusBadge = (status: string) => {
    switch (status) {
      case "PASSED":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-green-500 to-green-600 px-3 py-1.5 text-sm font-bold text-white shadow-md">
            <FiCheckCircle className="h-4 w-4" />
            PASSED
          </span>
        );
      case "FAILED":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 px-3 py-1.5 text-sm font-bold text-white shadow-md">
            <FiXCircle className="h-4 w-4" />
            FAILED
          </span>
        );
      case "IN_PROGRESS":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-1.5 text-sm font-bold text-white shadow-md">
            <FiClock className="h-4 w-4" />
            IN PROGRESS
          </span>
        );
      case "NOT_STARTED":
        return (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-gray-400 to-gray-500 px-3 py-1.5 text-sm font-bold text-white shadow-md">
            NOT STARTED
          </span>
        );
      default:
        return null;
    }
  };

  const getOutcomeBadge = (item: any) => {
    if (item.status === "NOT_APPLICABLE") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-500 border border-gray-200">
          N/A
        </span>
      );
    }

    const performed =
      item.status === "COMPLETED" ||
      item.status === "FAILED";

    if (!performed) return null;

    const failed =
      item.status === "FAILED" ||
      item.bug_found === true ||
      getActiveLinkedReportIds(item).length > 0;

    if (failed) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 border border-red-200">
          <FiXCircle className="h-3 w-3" />
          Failed
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200">
        <FiCheckCircle className="h-3 w-3" />
        Passed
      </span>
    );
  };

  // Group items by category (after filter)
const groupedItems = filteredGroupedItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Context Breadcrumb */}
        <div className="bg-white border-b border-gray-200 px-6 py-2.5 rounded-t-xl">
          <div className="mx-auto max-w-7xl flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
            <button 
              onClick={() => navigate(`/dashboard/programs/${programId}`)}
              className="hover:text-blue-600 transition-colors flex items-center gap-1"
            >
              <FiHome className="w-3 h-3" />
              {program?.title || "Program"}
            </button>
            <FiChevronRight className="text-gray-300" />
            {runId ? (
              <div className="flex items-center gap-2">
                <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-100 flex items-center gap-1">
                  <FiPlay className="w-2.5 h-2.5" />
                  Run: {currentRun?.run_title || `#${runId}`}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-[#58CC02] bg-green-50 px-2 py-0.5 rounded border border-green-100 flex items-center gap-1">
                  <FiLayers className="w-2.5 h-2.5" />
                  Master Checklist
                </span>
              </div>
            )}
            <FiChevronRight className="text-gray-300" />
            <span className="text-gray-900">{checklist.checklist_name}</span>
          </div>
        </div>

        {/* Header */}
        <div className="bg-white px-6 py-6 shadow-sm rounded-b-xl">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-tight">
                  {checklist.checklist_name}
                </h1>
                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest border ${runId ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-green-50 text-[#58CC02] border-[#58CC02]/30'}`}>
                   {runId ? 'Run Execution' : 'Master Template'}
                </span>
              </div>
              {checklist.description && (
                <p className="mt-2 text-sm font-medium leading-relaxed text-gray-500 max-w-3xl">
                  {checklist.description}
                </p>
              )}
            </div>
            
            <div className="flex items-center gap-4">
               {checklist?.is_admin_reviewed && (
                 <button
                   onClick={handleDownloadPDF}
                   disabled={isDownloadingPDF}
                   className="flex items-center gap-2 px-4 py-2 bg-[#010D2C] hover:bg-slate-800 text-white text-xs font-bold rounded-lg shadow-sm transition-all hover:shadow hover:translate-y-[-1px] disabled:opacity-50"
                 >
                   {isDownloadingPDF ? (
                     <>
                       <div className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                       <span>Generating PDF…</span>
                     </>
                   ) : (
                     <>
                       <FiDownload className="w-4 h-4 text-emerald-400" />
                       <span>Download PDF</span>
                     </>
                   )}
                 </button>
               )}
               <div className="text-right hidden sm:block">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Verification Status</p>
                  <p className="text-sm font-black text-gray-900">{completionPercentage}% Completed</p>
               </div>
               <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-xl flex items-center justify-center shadow-inner">
                  <FiLayers className={`w-6 h-6 ${runId ? 'text-blue-500' : 'text-[#58CC02]'}`} />
               </div>
            </div>
          </div>
        </div>

        {/* NEW Completion Status Bar */}
        <div className="rounded-xl bg-[#010D2C] p-6 shadow-xl border border-slate-800">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            {/* Left Section: Circular Progress */}
            <div className="flex items-center gap-6">
              <div className="relative flex h-20 w-20 items-center justify-center">
                <svg className="h-full w-full -rotate-90">
                  <circle
                    className="text-slate-700"
                    strokeWidth="6"
                    stroke="currentColor"
                    fill="transparent"
                    r="34"
                    cx="40"
                    cy="40"
                  />
                  <circle
                    className="text-[#58CC02] transition-all duration-1000 ease-out"
                    strokeWidth="6"
                    strokeDasharray={2 * Math.PI * 34}
                    strokeDashoffset={2 * Math.PI * 34 * (1 - completionPercentage / 100)}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r="34"
                    cx="40"
                    cy="40"
                  />
                </svg>
                <span className="absolute text-lg font-black text-white">{completionPercentage}%</span>
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">Completion Status</h3>
                <p className="text-sm font-medium text-slate-400">{totalItems} Total Cases</p>
              </div>
            </div>

            {/* Center Section: Progress Bar */}
            <div className="flex-1 w-full max-w-xl">
              <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
                <div 
                  className="h-full bg-white transition-all duration-1000 ease-out"
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            </div>

            {/* Right Section: Stats Row */}
            <div className="flex items-center gap-12">
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Performed</p>
                <p className="text-xl font-black text-emerald-500">{performedItems.length}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Passed</p>
                <p className="text-xl font-black text-blue-400">{passedItems}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Failed</p>
                <p className="text-xl font-black text-red-500">{failedItems}</p>
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">N/A</p>
                <p className="text-xl font-black text-orange-400">{naItems}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Test Cases */}
        <div className="space-y-4">
          {/* Filter Section */}
          <div className="rounded-2xl bg-white px-6 py-5 border border-slate-100 shadow-sm overflow-x-auto no-scrollbar">
            <div className="flex items-center justify-between gap-6 min-w-[1000px]">
              {/* Left: View Toggle */}
              <div className="flex items-center rounded-xl bg-slate-50 p-1 flex-shrink-0">
                <button
                  onClick={() => setViewMode("board")}
                  className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase transition-all rounded-lg ${
                    viewMode === "board" ? "bg-white text-gray-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  Board
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase transition-all rounded-lg ${
                    viewMode === "list" ? "bg-white text-gray-900 shadow-sm" : "text-slate-400 hover:text-slate-600"
                  }`}
                >
                  List
                </button>
              </div>

              {/* Center: Search and Filters */}
              <div className="flex items-center gap-6 flex-1 justify-center">
                {/* Search */}
                <div className="relative min-w-[280px] max-w-[400px] flex-1">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={testFilter}
                    onChange={(e) => setTestFilter(e.target.value)}
                    placeholder="Search items by ID or title..."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-11 pr-4 text-sm transition-all focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-0"
                  />
                </div>

                {/* Category Dropdown */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Category</span>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium focus:outline-none focus:border-blue-400 min-w-[140px]"
                  >
                    <option value="ALL">All Categories</option>
                    {uniqueCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {/* Status Dropdown */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</span>
                  <select
                    value={outcomeFilter}
                    onChange={(e) => setOutcomeFilter(e.target.value as any)}
                    className={`rounded-xl border px-4 py-2 text-sm font-medium focus:outline-none transition-all ${
                      outcomeFilter !== "ALL" ? 'bg-[#f0fdf4] border-[#58CC02] text-[#58CC02]' : 'bg-gray-50 border-gray-200 text-gray-700 focus:border-blue-400'
                    }`}
                  >
                    <option value="ALL">All Outcomes</option>
                    <option value="PASSED">Passed</option>
                    <option value="FAILED">Failed</option>
                    <option value="NOT_APPLICABLE">N/A</option>
                  </select>
                </div>
              </div>


            </div>
          </div>

          {/* Kanban Board View */}
          {viewMode === "board" && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Not Started Column */}
              <div className="flex flex-col rounded-lg bg-gray-100 ">
                <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-gray-600"></div>
                    <h3 className="font-semibold text-gray-900">Not Started</h3>
                  </div>
                  <span className="rounded-full bg-gray-300 px-2.5 py-0.5 text-xs font-bold text-gray-700">
                    {filteredBoardItems.filter(item => item.status === "NOT_STARTED").length}
                  </span>
                </div>
                <div className="flex-1 space-y-3 p-3">
                  {filteredBoardItems
                    .filter(item => item.status === "NOT_STARTED")
                    .map((item: any) => {
                      return (
                        <div key={item.item_id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                          <div className="mb-2 text-xs font-medium text-gray-500">
                            {item.category || "Uncategorized"}
                          </div>
                          <h4 className="mb-2 font-bold text-gray-900 break-all">
                            {item.title || item.category || "Test case"}
                          </h4>
                          {item.description && (
                            <p className="mb-3 text-xs text-gray-600 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>


              {/* Completed Column */}
              <div className="flex flex-col rounded-lg bg-green-50">
                <div className="flex items-center justify-between rounded-t-lg bg-green-100 px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FiCheckCircle className="h-4 w-4 text-green-600" />
                    <h3 className="font-semibold text-green-900">Completed</h3>
                  </div>
                  <span className="rounded-full bg-green-200 px-2.5 py-0.5 text-xs font-bold text-green-700">
                    {filteredBoardItems.filter(item => ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.status)).length}
                  </span>
                </div>
                <div className="flex-1 space-y-3 p-3">
                  {filteredBoardItems
                    .filter(item => ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.status))
                    .map((item: any) => {
                      const activeReportIds = getActiveLinkedReportIds(item);
                      const hasReports = activeReportIds.length > 0;
                      const canSeeDetails = canBusinessSeeReportDetails(item);
                      const outcome = getItemOutcome(item);

                      return (
                        <div
                          key={item.item_id}
                          className={`rounded-lg border-2 p-4 shadow-sm ${
                            outcome === "FAILED"
                              ? "border-red-500 bg-white"
                              : outcome === "NOT_APPLICABLE"
                              ? "border-gray-300 bg-slate-50"
                              : "border-green-500 bg-white"
                          }`}
                        >
                          <div className="mb-2 text-xs font-medium text-gray-500">
                            {item.category || "Uncategorized"}
                          </div>
                          <h4 className="mb-2 font-bold text-gray-900 break-all">
                            {item.title || item.category || "Test case"}
                          </h4>
                          {item.description && (
                            <p className="mb-3 text-xs text-gray-600 line-clamp-2">
                              {item.description}
                            </p>
                          )}

                          {/* Outcome Badge */}
                          {outcome === "FAILED" ? (
                            <div className="mb-3 inline-flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">
                              <FiAlertCircle className="h-3 w-3" />
                              Failed
                            </div>
                          ) : outcome === "NOT_APPLICABLE" ? (
                            <div className="mb-3 inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-500">
                              N/A
                            </div>
                          ) : (
                            <div className="mb-3 inline-flex items-center gap-1 rounded bg-green-100 px-2 py-1 text-xs font-semibold text-green-700">
                              <FiCheckCircle className="h-3 w-3" />
                              Passed
                            </div>
                          )}

                          {/* Vulnerabilities */}
                          {hasReports && canSeeDetails && (
                            <div className="border-t border-gray-200 pt-3 space-y-2">
                              <div className="flex items-center gap-2 text-xs text-red-600 mb-2">
                                <FiAlertCircle className="h-3.5 w-3.5" />
                                <span className="font-semibold">
                                  {activeReportIds.length} Vulnerabilit{activeReportIds.length > 1 ? 'ies' : 'y'}
                                </span>
                              </div>
                              {activeReportIds.map((reportId: number) => {
                               const report = linkedReportsById[reportId];
                               const isRestricted = !report || [ReportState.UNDER_REVIEW, ReportState.RECEIVED, 'Draft', 'received', 'underReview'].includes(report.state as any);
                               
                               if (isRestricted) {
                                 return (
                                   <div 
                                     key={reportId}
                                     className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg flex items-center justify-between opacity-70 cursor-help"
                                     title="Report is undergoing administrative review and is not yet available for viewing."
                                   >
                                     <div className="flex items-center gap-2">
                                       <FiLock className="h-3 w-3 text-slate-400" />
                                       <span className="text-[10px] font-bold text-slate-500">#{reportId}</span>
                                     </div>
                                     <span className="text-[9px] font-black italic text-slate-400 uppercase">Admin Review</span>
                                   </div>
                                 );
                               }

                               return (
                                 <button
                                   key={reportId}
                                   onClick={() => navigate(`/dashboard/reports/${reportId}`)}
                                   className="w-full text-left transition-all hover:bg-red-50/50 rounded-lg p-2 border border-red-100 hover:border-red-200 hover:shadow-sm"
                                 >
                                   <div className="flex items-center justify-between">
                                     <span className="text-xs font-bold text-red-700">#{reportId}</span>
                                     <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-black text-red-700 uppercase">
                                       {report.severityCategory || 'MEDIUM'}
                                     </span>
                                   </div>
                                   <p className="mt-1 text-[10px] font-medium text-slate-700 truncate">{report.title}</p>
                                 </button>
                               );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* List View - High Density Premium Table */}
          {viewMode === "list" && (
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
               {/* Table Header */}
               <div className="bg-slate-50/80 px-6 py-3.5 flex items-center border-b border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-[0.15em]">
                  <div className="flex-1">Security Requirement & Control</div>
                  <div className="w-[140px] px-4 text-center">Status / Outcome</div>
                  <div className="w-[140px] text-right">Linked Findings</div>
               </div>


               <div className="divide-y divide-slate-100">
                 {Object.entries(groupedItems).map(([category, categoryItems]) => {
                   const totalInCategory = categoryItems.length;
                   const completedInCategory = categoryItems.filter(item => ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.status)).length;
                   const catStatus = categoryStatus[category] || { status: completedInCategory === totalInCategory ? 'PASSED' : 'IN_PROGRESS' };

                   return (
                     <React.Fragment key={category}>
                       {/* Category Header Row */}
                       <div className="bg-slate-50/30 px-6 py-3 border-y border-slate-100 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                             <div className="w-1.5 h-5 rounded-full bg-[#010D2C]"></div>
                             <h4 className="text-[14px] font-black text-slate-900 uppercase tracking-widest">{category}</h4>
                          </div>
                       </div>

                     {/* Item Rows */}
                     {categoryItems.map((item) => {
                       const activeReportIds = getActiveLinkedReportIds(item);
                       const hasReports = activeReportIds.length > 0;
                       const canSeeDetails = canBusinessSeeReportDetails(item);
                       const outcome = getItemOutcome(item);
                       const isExpanded = expandedReports[item.program_item_id];

                       return (
                         <div key={item.program_item_id} className="group transition-all">
                           <div className="px-6 py-4 flex flex-col lg:flex-row lg:items-center hover:bg-slate-50/50 transition-colors">
                              {/* Requirement Description */}
                              <div className="flex-1 min-w-0 pr-6">
                                 <h5 className="text-[13px] font-bold text-slate-900 leading-snug mb-1 group-hover:text-blue-600 transition-colors break-all">
                                   {item.title || item.category || "Security Control"}
                                 </h5>
                                 {item.description && (
                                   <p className="text-[11px] text-slate-500 font-medium line-clamp-1 opacity-80">{item.description}</p>
                                 )}
                              </div>

                              {/* Status / Outcome */}
                              <div className="w-full lg:w-[140px] px-0 lg:px-4 py-3 lg:py-0 flex items-center lg:justify-center border-t lg:border-t-0 lg:border-l border-slate-50 mt-3 lg:mt-0">
                                 <div className="scale-90 origin-left lg:origin-center">
                                    {getOutcomeBadge(item) || getStatusBadge(item.status)}
                                 </div>
                              </div>



                              {/* Linked Findings */}
                              <div className="w-full lg:w-[140px] py-3 lg:py-0 pl-0 lg:pl-4 border-t lg:border-t-0 lg:border-l border-slate-50 flex items-center justify-end">
                                 {hasReports ? (
                                   canSeeDetails ? (
                                      <button
                                        onClick={() => setExpandedReports(prev => ({ ...prev, [item.program_item_id]: !prev[item.program_item_id] }))}
                                        className={`flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border ${isExpanded ? 'bg-red-600 text-white border-red-500 shadow-md' : 'bg-red-50 text-red-600 border-red-100 hover:bg-red-100 hover:shadow-sm'}`}
                                      >
                                         <FiAlertCircle className="w-3 h-3" />
                                         {item.linked_report_ids?.length || 0} {isExpanded ? 'Hide' : 'Findings'}
                                      </button>
                                   ) : (
                                      <div 
                                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[9px] font-black text-slate-400 uppercase tracking-widest cursor-help"
                                        title="Linked reports are undergoing administrative review and will be available once validated."
                                      >
                                         <FiLock className="w-3 h-3" />
                                         Locked
                                      </div>
                                   )
                                 ) : (
                                   <div className="px-3 py-1.5 text-[9px] font-bold text-slate-300 uppercase tracking-widest">No Findings</div>
                                 )}
                              </div>
                           </div>

                           {/* Expanded Report View */}
                           {isExpanded && activeReportIds.length > 0 && (
                             <div className="bg-slate-100/50 px-6 py-4 border-t border-slate-100 animate-in slide-in-from-top-1 duration-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                   {activeReportIds.map(reportId => {
                                      const report = linkedReportsById[reportId];
                                      const isRestricted = !report || [ReportState.UNDER_REVIEW, ReportState.RECEIVED, 'Draft', 'received', 'underReview'].includes(report.state as any);

                                      if (isRestricted) {
                                        return (
                                          <div 
                                            key={reportId}
                                            className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-xl opacity-80 cursor-help shadow-inner"
                                            title="Report is undergoing administrative review and is not yet available for viewing."
                                          >
                                             <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center text-slate-500 border border-slate-300">
                                                   <span className="text-[10px] font-black">#{reportId}</span>
                                                </div>
                                                <div>
                                                   <p className="text-[11px] font-bold text-slate-400 italic">
                                                      [Pending Administrative Review]
                                                   </p>
                                                   <div className="flex items-center gap-1.5 mt-0.5">
                                                      <FiLock className="w-2.5 h-2.5 text-slate-300" />
                                                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Access Restricted</span>
                                                   </div>
                                                </div>
                                             </div>
                                          </div>
                                        );
                                      }

                                      return (
                                        <button
                                          key={reportId}
                                          onClick={() => navigate(`/dashboard/reports/${reportId}`)}
                                          className="flex items-center justify-between p-3 bg-white border border-red-100 rounded-xl hover:shadow-lg hover:border-red-200 transition-all text-left group/report"
                                        >
                                           <div className="flex items-center gap-3">
                                              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center text-red-600 border border-red-100 group-hover/report:bg-red-600 group-hover/report:text-white transition-colors">
                                                 <span className="text-[10px] font-black">#{reportId}</span>
                                              </div>
                                              <div>
                                                 <p className="text-[11px] font-bold text-slate-900 group-hover/report:text-blue-600 truncate max-w-[200px]">
                                                    {report?.title || `Linked Vulnerability #${reportId}`}
                                                 </p>
                                                 <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none">Validated Record</span>
                                              </div>
                                           </div>
                                           {report?.severityCategory && (
                                              <span className="px-2 py-1 bg-red-100 text-red-700 text-[8px] font-black uppercase tracking-tighter rounded-md border border-red-200">
                                                 {report.severityCategory}
                                              </span>
                                           )}
                                        </button>
                                      );
                                   })}
                                </div>
                             </div>
                           )}
                         </div>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await executeDownloadPDF();
        }}
        title="Testing Incomplete"
        message={`This checklist is only ${completionPercentage}% completed. Are you sure you want to download the PDF report before all test cases have been finalized?`}
        confirmLabel="Download Anyway"
        cancelLabel="Cancel"
        type="warning"
      />
    </div>
  );
};

export default BusinessChecklistView;

